import React, { useEffect, useMemo, useState } from 'react'
import { Edit, Visibility } from '@mui/icons-material';
import { Alert, AlertTitle, Chip, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import MergeIcon from "@mui/icons-material/CallMerge";
import ErrorIcon from "@mui/icons-material/DoDisturbAlt";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { getPossibleOrganizationsForCollapse, togglesystemOrganization } from '../../services/organisation.service';
import { issueResponseMessage } from '../../actions/message';
import ConfirmationModal from '../modals/confirmation';
import CollapseOrganizationModal from '../modals/collapseOrganizationModal';
import MuiDataGrid from './MuiDataGrid';
import { ResponsiveRow } from '../../constants/layout.constants';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';

function ManageOrgDuplicatesTab() {
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapseOrganizationModalOpen, setCollapseOrganizationModalOpen] =
    useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [task, setTask] = useState(null);
  const [confirm, setConfirm] = useState(false);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const possibleOrganizations = await getPossibleOrganizationsForCollapse();
      setOrganizations(possibleOrganizations);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleViewEditMergeClick = (org, action) => {
    // Handle view click logic here (e.g., open a modal to view details)
    setTask(action);
    setCollapseOrganizationModalOpen(true);
    setSelectedOrganization(org);
  };

  const handleCollapseOrganizationModalClose = () => {
    setCollapseOrganizationModalOpen(false);
    setSelectedOrganization(null);
    fetchOrganizations();
  };

  const handleTogglesystemOrganization = async (id) => {
    try {
      const res = await togglesystemOrganization(id);
      issueResponseMessage(res);
      handleConfirmClose();
      fetchOrganizations();
    } catch (error) {
      console.error("Error making organization a system organization:", error);
    }
  };

  const handleConfirmClose = () => {
    setConfirm(false);
    setSelectedOrganization(null);
  };

  const columns = useMemo(() => [
    {
      headerName: "Name",
      field: "name",
      minWidth: 200,
      flex: 2,
    },
    {
      headerName: "Description",
      field: "description",
      minWidth: 150,
      flex: 1.5,
    },
    { headerName: "Status", field: "status", minWidth: 200, flex: 2 },
    {
      headerName: "System Organization",
      field: "systemOrganization",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Chip
            icon={
              params.row.systemOrganization ? (
                <VerifiedUserIcon />
              ) : (
                <ErrorIcon />
              )
            }
            label={params.row.systemOrganization ? "Yes" : "No"}
            color={params.row.systemOrganization ? "success" : "error"}
            size="small"
            sx={{ flexShrink: 0 }}
          />
        );
      },
    },
    {
      headerName: "Has Address",
      field: "addressId",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              params.row.addressId ? params.row.address.fullAddress1 : "No"
            }
          >
            <Chip
              icon={params.row.addressId ? <VerifiedUserIcon /> : <ErrorIcon />}
              label={
                params.row.addressId ? params.row.address.fullAddress1 : "No"
              }
              color={params.row.addressId ? "success" : "error"}
              size="small"
              sx={{ flexShrink: 0 }}
            />
          </Tooltip>
        );
      },
    },
    {
      headerName: "Has Contacts",
      field: "contact",
      minWidth: 175,
      flex: 1.75,
      renderCell: (params) => {
        return (
          <Tooltip
            title={
              params.row.contact && params.row.contact.length > 0
                ? params.row.contact.map((contact) => contact.name).join(", ")
                : "No"
            }
          >
            <Chip
              icon={
                params.row.contact && params.row.contact.length > 0 ? (
                  <VerifiedUserIcon />
                ) : (
                  <ErrorIcon />
                )
              }
              label={
                params.row.contact && params.row.contact.length > 0
                  ? params.row.contact.map((contact) => contact.name).join(", ")
                  : "No"
              }
              color={
                params.row.contact && params.row.contact.length > 0
                  ? "success"
                  : "error"
              }
              size="small"
              sx={{ flexShrink: 0 }}
            />
          </Tooltip>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 100,
      type: "actions",
      getActions: (params) => {
        let arr = [];
        arr.push(
          <GridActionsCellItem
            icon={<Visibility />}
            onClick={() => handleViewEditMergeClick(params.row, "view")}
            label="View"
            showInMenu
          />,
        );
        arr.push(
          <GridActionsCellItem
            icon={<Edit />}
            onClick={() => handleViewEditMergeClick(params.row, "edit")}
            label="Edit"
            showInMenu
          />,
        );
        if (!params.row.systemOrganization) {
          arr.push(
            <GridActionsCellItem
              icon={<MergeIcon />}
              label="Merge Company"
              onClick={() => handleViewEditMergeClick(params.row, "merge")}
              showInMenu
            />,
          );
        }
        arr.push(
          <GridActionsCellItem
            icon={<CorporateFareIcon />}
            label={params.row.systemOrganization ? "Remove as System Organization" : "Add as System Organization"}
            onClick={() => {
              setSelectedOrganization(params.row);
              setConfirm(true);
            }}
            showInMenu
          />,
        );
        return arr;
      },
    },
  ]);
  return (
    <ResponsiveRow sx={{ marginBlock: 4 }}>
      <Typography
        variant="h4"
        component="h1"
        gutterBottom
        sx={{ textAlign: { xs: "center", md: "left" }, width: "100%" }}
      >
        Potential Duplicates for Review (
        {organizations ? organizations.length : 0})
      </Typography>
      <Alert
        severity="info"
        sx={{
          mb: 2,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <AlertTitle>Info</AlertTitle>
        Please review the potential duplicates and merge them if necessary.
        <br />A merge will update the primary company with the secondary
        company's information. A system organization cannot be merged into
        another company.
      </Alert>
      <MuiDataGrid
        dataGridColumns={columns}
        dataGridRows={organizations}
        loading={loading}
        height="65vh"
        totalRows={organizations? organizations.length :0}
        serverPagination={false}
        noSelection
      />
      {task && collapseOrganizationModalOpen && (
        <CollapseOrganizationModal
          open={collapseOrganizationModalOpen}
          onClose={handleCollapseOrganizationModalClose}
          organization={selectedOrganization}
          task={task}
        />
      )}
      {confirm && (
        <ConfirmationModal
          showConfirmationModal={confirm}
          handleConfirmationClose={handleConfirmClose}
          title={"Confirm toggle of system organization status"}
          severity={"warning"}
          body={
            `Are you sure you want to ${selectedOrganization.systemOrganization ? "remove" : "add"} system organization status for organization: ` +
            selectedOrganization.name
          }
          positiveText={"Confirm"}
          negativeText={"Cancel"}
          positiveAction={() =>
            handleTogglesystemOrganization(selectedOrganization.id)
          }
          negativeAction={handleConfirmClose}
        />
      )}
    </ResponsiveRow>
  );
}

export default ManageOrgDuplicatesTab